import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Send, Video, Phone, ArrowLeft, Paperclip } from 'lucide-react';

interface Message {
  id: string;
  sender: 'doctor' | 'patient';
  text: string;
  time: string;
}

interface MessagingScreenProps {
  onBack?: () => void;
}

export function MessagingScreen({ onBack }: MessagingScreenProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      sender: 'doctor',
      text: 'Good morning! How are you feeling today?',
      time: '9:30 AM',
    },
    {
      id: '2',
      sender: 'patient',
      text: 'Good morning, Doctor. I\'m feeling much better! Completed my morning exercises.',
      time: '9:35 AM',
    },
    {
      id: '3',
      sender: 'doctor',
      text: 'Excellent! I can see your SpO₂ levels are improving. Keep up the good work.',
      time: '9:40 AM',
    },
    {
      id: '4',
      sender: 'patient',
      text: 'Thank you! Should I increase the duration of my breathing exercises?',
      time: '9:42 AM',
    },
    {
      id: '5',
      sender: 'doctor',
      text: 'Let\'s maintain the current schedule for this week. We\'ll review and adjust next Monday.',
      time: '9:45 AM',
    },
  ]);

  const [newMessage, setNewMessage] = useState('');

  const handleSend = () => {
    if (newMessage.trim()) {
      const message: Message = {
        id: Date.now().toString(),
        sender: 'patient',
        text: newMessage,
        time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      };
      setMessages([...messages, message]);
      setNewMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="min-h-screen bg-[#D4F0F8] flex flex-col">
      {/* Header */}
      <div className="bg-primary p-4 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <button onClick={onBack} className="text-primary-foreground">
                <ArrowLeft className="w-6 h-6" />
              </button>
            )}
            <Avatar className="w-12 h-12 bg-accent">
              <AvatarFallback className="text-accent-foreground">DM</AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-primary-foreground">Dr. Meera Krishnan</h2>
              <p className="text-primary-foreground/70">Pulmonology Specialist</p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              className="p-2 hover:bg-primary-foreground/10 rounded-full transition-colors"
              onClick={() => alert('Video call feature would be implemented here')}
            >
              <Video className="w-6 h-6 text-primary-foreground" />
            </button>
            <button
              className="p-2 hover:bg-primary-foreground/10 rounded-full transition-colors"
              onClick={() => alert('Voice call feature would be implemented here')}
            >
              <Phone className="w-6 h-6 text-primary-foreground" />
            </button>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-24">
        <div className="text-center">
          <p className="text-muted-foreground">Today</p>
        </div>

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'patient' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[75%] ${
                message.sender === 'patient' ? 'order-2' : 'order-1'
              }`}
            >
              <div
                className={`p-4 rounded-2xl ${
                  message.sender === 'patient'
                    ? 'bg-secondary text-foreground rounded-br-sm'
                    : 'bg-primary text-primary-foreground rounded-bl-sm'
                }`}
              >
                <p className="break-words">{message.text}</p>
              </div>
              <p
                className={`text-muted-foreground mt-1 ${
                  message.sender === 'patient' ? 'text-right' : 'text-left'
                }`}
              >
                {message.time}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Video Call Banner (Optional) */}
      <div className="mx-4 mb-4">
        <Card className="bg-gradient-to-r from-primary to-[#14CAF4] border-none shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-primary-foreground">Schedule Teleconsultation</h3>
                <p className="text-primary-foreground/80">
                  Connect face-to-face with your doctor
                </p>
              </div>
              <Button
                className="bg-accent hover:bg-accent/90 rounded-xl"
                onClick={() => alert('Video consultation scheduling would be implemented here')}
              >
                <Video className="w-4 h-4 mr-2" />
                Schedule
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Input Area */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4 shadow-lg">
        <div className="flex items-end gap-2 max-w-4xl mx-auto">
          <button className="p-2 hover:bg-secondary rounded-full transition-colors">
            <Paperclip className="w-6 h-6 text-muted-foreground" />
          </button>

          <div className="flex-1 relative">
            <Input
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              className="rounded-2xl pr-12 min-h-12 resize-none"
            />
          </div>

          <button
            onClick={handleSend}
            disabled={!newMessage.trim()}
            className={`p-3 rounded-full transition-colors ${
              newMessage.trim()
                ? 'bg-accent hover:bg-accent/90 text-accent-foreground'
                : 'bg-secondary text-muted-foreground'
            }`}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>

        {/* Quick Responses */}
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2 max-w-4xl mx-auto">
          <button
            onClick={() => setNewMessage('I completed today\'s exercises')}
            className="px-4 py-2 bg-secondary rounded-full whitespace-nowrap hover:bg-secondary/80 transition-colors"
          >
            ✅ Completed exercises
          </button>
          <button
            onClick={() => setNewMessage('I have a question about my medication')}
            className="px-4 py-2 bg-secondary rounded-full whitespace-nowrap hover:bg-secondary/80 transition-colors"
          >
            💊 Medication question
          </button>
          <button
            onClick={() => setNewMessage('Can we reschedule my appointment?')}
            className="px-4 py-2 bg-secondary rounded-full whitespace-nowrap hover:bg-secondary/80 transition-colors"
          >
            📅 Reschedule
          </button>
        </div>
      </div>
    </div>
  );
}
